import { Injectable } from "@angular/core";
import { Http } from "@angular/http";
import { BehaviorSubject } from "rxjs/Rx";
import { User, TripHistoryDatabase } from "@shared/models";
import { Constants } from "@app/constants";
import { MatPaginator } from "@angular/material";

@Injectable()
export class UserService {
  private loggedInSource = new BehaviorSubject<boolean>(false);
  private currentUser = new BehaviorSubject<User>(
    new User("", "", "", "", 0, Array(), false, false, "")
  );
  private tripHistoryDatabase = new BehaviorSubject<TripHistoryDatabase>(
    new TripHistoryDatabase()
  );
  private tripHistoryDatabaseLength = new BehaviorSubject<number>(0);

  // observable boolean stream
  loggedInChange$ = this.loggedInSource.asObservable();
  userChange$ = this.currentUser.asObservable();
  tripDetailsChange$ = this.tripHistoryDatabase.asObservable();
  tripDetailsLengthChange$ = this.tripHistoryDatabaseLength.asObservable();

  private static determineTypeOfCustomerIds(loginResponse) {
    if (typeof loginResponse.customerIdList === "string") {
      return JSON.parse(loginResponse.customerIdList);
    } else {
      return loginResponse.customerIdList;
    }
  }

  constructor(private http: Http) {}

  setUserData(loginResponse) {
    let accessToken;
    if (
      localStorage.getItem(Constants.TOKEN) ||
      sessionStorage.getItem(Constants.TOKEN)
    ) {
      accessToken = localStorage.getItem(Constants.TOKEN)
        ? localStorage.getItem(Constants.TOKEN)
        : sessionStorage.getItem(Constants.TOKEN);
    } else {
      accessToken = loginResponse.access_token;
    }
    // TODO send me back some JSON for the customerIdList
    if (typeof loginResponse.emailConfirmed === "string") {
      if (loginResponse.emailConfirmed === "True") {
        loginResponse.emailConfirmed = true;
      } else {
        loginResponse.emailConfirmed = false;
      }
    }

    if (typeof loginResponse.customerService === "string") {
      if (loginResponse.customerService === "True") {
        loginResponse.customerService = true;
      } else {
        loginResponse.customerService = false;
      }
    }

    this.changeUser(
      new User(
        loginResponse.userName,
        loginResponse.email,
        accessToken,
        "",
        1,
        UserService.determineTypeOfCustomerIds(loginResponse),
        !!loginResponse.emailConfirmed,
        !!loginResponse.customerService,
        loginResponse.group
      )
    );
    this.changeLogInStatus(true);
    return this.userChange$;
  }

  setAccountSummaryArray(accountSummary) {
    this.currentUser.value.accountSummaryArray = accountSummary;
    this.changeUser(this.currentUser.value);
  }

  createAccountDetailArray(id) {
    this.currentUser.value.accountDetailsList[id] = new Array();
  }

  setAccountIndex(index) {
    let user = this.currentUser.value;
    user.accountIndex = index;
    this.changeUser(user);
  }

  setCustomerIndex(index) {
    this.currentUser.value.setCustomerIndex(index);
    this.setAccountIndex(0);
  }

  setCreditCardId(cCardId) {
    this.currentUser.value.creditCardId = cCardId;
  }

  addCreditCard(cardId, cardNumber, expiryDate, cvv, isPrimary, token) {
    this.currentUser.value.addCreditCard(
      cardId,
      cardNumber,
      expiryDate,
      cvv,
      isPrimary,
      token
    );
  }

  emptyCreditCardArray() {
    this.setCreditCardArrayLength(0);
  }

  setCreditCardArrayLength(length) {
    this.currentUser.value.creditCardArray.length = length;
  }
  emptyCreditHistoryArray() {
    this.currentUser.value.creditHistoryArray.length = 0;
  }
  emptyPaymentHistoryArray() {
    this.currentUser.value.paymentHistoryArray.length = 0;
  }

  addPayment(
    id: string,
    cardNumber: string,
    paymentDate: string,
    paymentAmount: number,
    authCode: string
  ) {
    this.currentUser.value.addPayment(
      id,
      cardNumber,
      paymentDate,
      paymentAmount,
      authCode
    );
  }

  addCredit(
    id: string,
    creditDate: string,
    creditAmount: number,
    creditType: string
  ) {
    this.currentUser.value.addCredit(id, creditDate, creditAmount, creditType);
  }

  addPass(cardId, passName, cardTypeId, ccNumber, closedDt, evExpiryDt) {
    this.currentUser.value.addPass(
      cardId,
      passName,
      cardTypeId,
      ccNumber,
      closedDt,
      evExpiryDt
    );
  }

  addCommercialPass(
    cardId,
    passName,
    cardTypeId,
    ccNumber,
    closedDt,
    description
  ) {
    this.currentUser.value.addPass(
      cardId,
      passName,
      cardTypeId,
      ccNumber,
      closedDt,
      description.split("(")[0]
    );
  }

  emptyAccountPassArray() {
    this.setPassArrayLength(0);
  }

  setPassArrayLength(length) {
    this.currentUser.value.accountPassArray.length = length;
  }

  setBillingAddress(
    nameOnCard: string,
    city: string,
    country: string,
    phone: string,
    postalCode: string,
    province: string,
    street: string
  ) {
    this.currentUser.value.customerIdArray[
      this.currentUser.value.customerIndex
    ].BillingAddress = {
      NameOnCard: nameOnCard,
      City: city,
      Street: street,
      Country: country,
      PostalCode: postalCode,
      Province: province,
      Phone: phone,
    };
  }

  setMailingAddress(
    country: string,
    city: string,
    email: string,
    phone1: string,
    phone2: string,
    postalCode: string,
    province: string,
    street: string, 
    street2: string
  ) {
    this.currentUser.value.customerIdArray[
      this.currentUser.value.customerIndex
    ].MailingAddress = {
      City: city,
      Country: country,
      Email: email,
      Phone1: phone1,
      Phone2: phone2,
      PostalCode: postalCode,
      Province: province,
      Street: street,
      Street2: street2
    };
  }

  addTripToAccountDetailArray(id, trip) {
    console.log(this.currentUser.value);
    console.log(id);
    if(this.currentUser.value.accountDetailsList[id] == null){
      this.currentUser.value.accountDetailsList[id] = [];
    }
    this.currentUser.value.accountDetailsList[id].push(trip);
  }

  addTripsToAccountDetailDatabase(trips) {
    this.tripHistoryDatabase.value.addTrips(trips);
    this.tripHistoryDatabase.next(this.tripHistoryDatabase.value);
    this.tripHistoryDatabaseLength.next(
      this.tripHistoryDatabase.value.data.length
    );
  }

  deleteTripsFromAccountDetailDatabase() {
    this.tripHistoryDatabase.value.removeTrips();
    this.tripHistoryDatabase.next(this.tripHistoryDatabase.value);
  }

  changeUser(user: User) {
    this.currentUser.next(user);
  }

  changeLogInStatus(loginstatus: boolean) {
    this.loggedInSource.next(loginstatus);
  }

  getUser() {
    return this.userChange$;
  }

  getTripDetailDatabase() {
    return this.tripDetailsChange$;
  }

  getTripDetailDatabaseLength() {
    return this.tripDetailsLengthChange$;
  }
}



// WEBPACK FOOTER //
// C:/Users/EricChanney/Apps/nexpress-angular-site/src/app/core/services/user.service.ts